import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    Box,
    Grid,
    TextField,
    MenuItem,
    Typography,
    Select,
    Checkbox,
    FormControlLabel,
    Button,
    FormControl,
    InputLabel,
} from "@mui/material";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import GenderToggle from "./GenderToggle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import useNavigateWithLang from "../hooks/useNavigateWithLang";
import {
    getEmptyGuest,
    getLocalizedRegionLabelFromLovCode,
    getLovForField,
    IDENTIFICATION_TYPES_MAP,
    isBlankEmail,
    normalizeGuestTitleToLovCode,
    sortCodeLabelEntriesByLabel,
} from "../utils/guestUtils";
import { getTenantConfig, getChildMaxAgeYears } from "../config/tenant";
import {
    AddressInfo,
    EmailInfo,
    GuestFormDataWithCompletion,
    TelephoneInfo,
} from "../models/StaticDataModel";
import { getBrandText } from "../styles/branding";
import { useBrandAppearance } from "../hooks/useBrandAppearance";
import { buildGuestPatchFromScan } from "../utils/scanToGuest";
import {
    dobPickerBounds,
    isGuestSlotChild,
    parseReservationArrivalDate,
    validateDobForAdultChildSlot,
    adultMinAgeYears,
} from "../utils/guestSlotAgeValidation";

const GuestDetailsForm: React.FC<{ guest: GuestFormDataWithCompletion | null }> = ({ guest }) => {
    const { colors: BrandColors } = useBrandAppearance();
    const BrandText = getBrandText();

    /** Underline / DatePicker value text — MUI defaults stay dark without this in dark mode */
    const standardInputValueSx = {
        "& .MuiInput-input": { color: "var(--oc-text)" },
        "& .MuiInputBase-input": { color: "var(--oc-text)" },
    };
    /** Popover list: same surface as fields, readable contrast */
    const selectMenuProps = {
        PaperProps: {
            sx: {
                maxHeight: 360,
                bgcolor: "var(--oc-paper-bg)",
                color: "var(--oc-text)",
                "& .MuiMenuItem-root": {
                    color: "var(--oc-text)",
                },
                "& .MuiMenuItem-root:hover": {
                    backgroundColor: "rgba(128, 128, 128, 0.22)",
                },
                "& .MuiMenuItem-root.Mui-selected": {
                    backgroundColor: "rgba(128, 128, 128, 0.3)",
                },
            },
        },
    };
    const { state, updateGuest, setIsFormValid, setIsFormDirty } =
        useContext(AppContext) ?? {};
    const { t: tGuestDetails, i18n } = useTranslation("guest_details");

    // Each LOV-driven dropdown reads its options from the per-field source configured on the
    // tenant (chain.lovSources). Defaults preserve the pre-V14 hard-wiring:
    //   nationality   -> NATIONALITY LOV (3-letter)
    //   placeOfBirth  -> COUNTRYCODES LOV (2-letter); submitted as birthCountry.code
    //   addressCountry-> COUNTRYCODES LOV (2-letter)
    // Operators can flip any of the three to NATIONALITY on the chain modal to opt into
    // 3-letter codes for the country dropdowns. The resolver also handles the empty-LOV
    // fallback (Jadranka-style "empty COUNTRYCODES -> use nationalities").
    const tenantLovSources = getTenantConfig()?.lovSources;
    const lovMaps = useMemo(
        () => ({ nationalities: state?.nationalities, countries: state?.countries }),
        [state?.nationalities, state?.countries]
    );

    const buildSortedEntries = useCallback((codes: string[]) => {
        const pairs = codes.map(
            (code) => [code, getLocalizedRegionLabelFromLovCode(code, i18n.language)] as [string, string]
        );
        return sortCodeLabelEntriesByLabel(pairs, i18n.language);
    }, [i18n.language]);

    const sortedNationalityEntries = useMemo(
        () => buildSortedEntries(Object.keys(getLovForField("nationality", tenantLovSources, lovMaps))),
        [buildSortedEntries, lovMaps, tenantLovSources]
    );
    const sortedBirthCountryEntries = useMemo(
        () => buildSortedEntries(Object.keys(getLovForField("birthCountry", tenantLovSources, lovMaps))),
        [buildSortedEntries, lovMaps, tenantLovSources]
    );
    const sortedAddressCountryEntries = useMemo(
        () => buildSortedEntries(Object.keys(getLovForField("addressCountry", tenantLovSources, lovMaps))),
        [buildSortedEntries, lovMaps, tenantLovSources]
    );
    const { guestId } = useParams<{ guestId: string }>();
    const navigate = useNavigate();
    const { getLocalizedPath } = useNavigateWithLang();
    const location = useLocation();
    const scannedData = location.state?.scannedData ? { ...location.state.scannedData } : undefined;

    // --- modal/show-state control
    const [hasAppliedScan, setHasAppliedScan] = useState(false);

    // --- form state
    const [formData, setFormData] = useState<GuestFormDataWithCompletion>(guest ?? getEmptyGuest());

    const guestSlotIndex = useMemo(() => {
        const n = Number(guestId);
        return Number.isFinite(n) && n >= 1 ? n - 1 : 0;
    }, [guestId]);
    const requireContact = guestSlotIndex === 0;
    // Prefer the kind resolved by the mapper (persisted ADULT/CHILD or DOB-derived) so a saved
    // guest is validated against its real kind even when sitting in an overflow slot (where the
    // absolute index alone would lie). Fall back to the legacy index rule for guests without a
    // resolved kind (e.g. brand-new placeholder slots).
    const isChildSlot = useMemo(() => {
        const persisted = guest?.guestKind ?? formData?.guestKind;
        if (persisted === "CHILD") return true;
        if (persisted === "ADULT") return false;
        return isGuestSlotChild(state?.adultCount, guestSlotIndex);
    }, [guest?.guestKind, formData?.guestKind, state?.adultCount, guestSlotIndex]);
    const childMaxAgeYears = getChildMaxAgeYears();
    const referenceArrival = useMemo(
        () => parseReservationArrivalDate(state?.arrivalDate),
        [state?.arrivalDate]
    );
    const dobSlotAgeIssue = useMemo(
        () => validateDobForAdultChildSlot(isChildSlot, formData.dateOfBirth, referenceArrival, childMaxAgeYears),
        [isChildSlot, formData.dateOfBirth, referenceArrival, childMaxAgeYears]
    );
    const dobPickerBoundsMemo = useMemo(
        () => dobPickerBounds(isChildSlot, referenceArrival, childMaxAgeYears),
        [referenceArrival, isChildSlot, childMaxAgeYears]
    );

    // Titles LOV is code → label; form state must hold the **code** for Opera UpdateName.
    useEffect(() => {
        const titles = state?.titles;
        if (!titles || Object.keys(titles).length === 0) return;
        setFormData((prev) => {
            const next = normalizeGuestTitleToLovCode(prev.title, titles);
            if (next === prev.title) return prev;
            return { ...prev, title: next };
        });
    }, [state?.titles]);

    const titleCodeForSelect = (() => {
        const titles = state?.titles;
        if (!titles || Object.keys(titles).length === 0) return "";
        return normalizeGuestTitleToLovCode(formData.title, titles);
    })();

    const genderForToggle: "male" | "female" = (() => {
        const g = (formData.gender ?? "").toString().trim().toUpperCase();
        if (g === "FEMALE" || g === "F") return "female";
        if (g === "MALE" || g === "M") return "male";
        return "male"; // final fallback
    })();

    useEffect(() => {
        if (!guest || hasAppliedScan) return;
        setFormData(guest);
        updateGuest?.(guest);
        // Initial hydration is not a user change → form is clean
        setIsFormDirty?.(false);
        // Setters are stable; including them would re-hydrate the form on every context change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guest, hasAppliedScan]);

    useEffect(() => {
        setHasAppliedScan(false);
    }, [guest?.id]);

    // Reset dirty flag when this form unmounts so other pages don't inherit it
    useEffect(() => {
        return () => {
            setIsFormDirty?.(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // derive modal visibility locally:
    // show if there is any prefilled data, no scan applied yet, and we didn't skip
    const hasAnyPrefill =
        !!formData?.firstName ||
        !!formData?.lastName ||
        !!formData?.emailInfo?.email ||
        !!formData?.telephoneInfo?.phoneNumber ||
        !!formData?.addressInfo?.addressLine ||
        !!formData?.addressInfo?.cityName ||
        !!formData?.addressInfo?.postalCode ||
        !!formData?.identificationInfo?.identification?.idNumber;

    // apply scanned data once, patching only fields present
    useEffect(() => {
        if (!scannedData || hasAppliedScan) return;

        // ✅ wait until form is hydrated (either guest loaded or current form has content)
        const hydrated = Boolean(guest) || hasAnyPrefill;
        if (!hydrated) return;

        try {
            // Pass the chain's LOV sources so the scanner knows whether to collapse the address
            // country to ISO-2 (default, COUNTRYCODES dropdown) or leave it as ISO-3 (when the
            // chain has flipped addressCountry to NATIONALITY).
            const patch = buildGuestPatchFromScan(
                (scannedData as any).Data || {},
                // handle either `Metadata` or `metadata` just in case
                (scannedData as any).Metadata || (scannedData as any).metadata || null,
                {
                    addressCountry: (tenantLovSources ?? undefined)?.addressCountry,
                    birthCountry: (tenantLovSources ?? undefined)?.birthCountry,
                }
            );
            if (patch && Object.keys(patch).length > 0) {
                // ✅ choose the safest base to preserve existing values
                const base = guest ?? formData;
                const merged = deepMergeDefined(base, patch);
                setFormData(merged);
                updateGuest?.(merged);
                // Document scan modified form data without saving — treat as dirty
                setIsFormDirty?.(true);
            }
        } catch (err) {
            console.error("Scan patch error:", err);
        } finally {
            setHasAppliedScan(true);
            // remove state from history so it won't re-apply on back/forward
            window.history.replaceState({}, document.title, window.location.pathname);
        }
        // deepMergeDefined is recreated each render; the scan should apply once per payload.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scannedData, hasAppliedScan, guest, hasAnyPrefill]);


    // ---- Put this tiny util in the same file or a shared utils file ----
    function isPlainObject(v: any) {
        return v && typeof v === "object" && !Array.isArray(v);
    }

    /**
     * Deep merge that only writes keys whose value is not undefined.
     * Arrays are replaced, not concatenated (fits your model).
     */
    function deepMergeDefined<T>(target: T, source: any): T {
        if (!isPlainObject(source)) return target;

        const out: any = Array.isArray(target) ? [...(target as any)] : { ...(target as any) };

        for (const key of Object.keys(source)) {
            const sv = source[key];
            if (sv === undefined) continue;

            if (isPlainObject(sv)) {
                out[key] = deepMergeDefined(out[key] ?? {}, sv);
            } else {
                // primitives / arrays: assign directly
                out[key] = sv;
            }
        }
        return out as T;
    }


    const isFormDisabled = false;

    // --- validation helpers + handlers
    const [emailError, setEmailError] = useState(false);
    const [emailHelperText, setEmailHelperText] = useState("");

    const handleChange = (field: keyof typeof formData, value: any) => {
        const updatedGuest = { ...formData, [field]: value };
        setFormData(updatedGuest);
        updateGuest?.(updatedGuest);
        setIsFormDirty?.(true);
    };

    const dateOfBirthValue = (): Dayjs | null => {
        const d = formData.dateOfBirth;
        if (!d?.day || !d?.month || !d?.year) return null;
        const parsed = dayjs(new Date(d.year, d.month - 1, d.day));
        return parsed.isValid() ? parsed : null;
    };

    const handleIdentificationChange = (field: "idType" | "idNumber", value: string) => {
        const current = formData.identificationInfo?.identification || {};
        const updatedGuest = {
            ...formData,
            identificationInfo: {
                type: "Document",
                id: formData.identificationInfo?.id || "",
                identification: {
                    ...current,
                    [field]: value,
                },
            },
        };
        setFormData(updatedGuest);
        updateGuest?.(updatedGuest);
        setIsFormDirty?.(true);
    };

    const handleAddressInfoChange = (field: keyof AddressInfo, value: string) => {
        const updatedGuest = {
            ...formData,
            addressInfo: {
                ...formData.addressInfo,
                [field]: value,
            },
        };
        setFormData(updatedGuest);
        updateGuest?.(updatedGuest);
        setIsFormDirty?.(true);
    };

    const handleCountryCodeChange = (value: string) => {
        const updatedGuest = {
            ...formData,
            addressInfo: {
                ...formData.addressInfo,
                country: value,
            },
        };
        setFormData(updatedGuest);
        updateGuest?.(updatedGuest);
        setIsFormDirty?.(true);
    };

    const handleEmailInfoChange = (field: keyof EmailInfo, value: string) => {
        const updatedGuest = {
            ...formData,
            emailInfo: {
                ...formData.emailInfo,
                [field]: value,
            },
        };
        setFormData(updatedGuest);
        updateGuest?.(updatedGuest);
        setIsFormDirty?.(true);

        if (field === "email") {
            if (!requireContact && isBlankEmail(value)) {
                setEmailError(false);
                setEmailHelperText("");
                return;
            }
            const isValid = validateEmail(value);
            setEmailError(!isValid);
            setEmailHelperText(!isValid ? tGuestDetails("emailErrorMessage") : "");
        }
    };

    const handleTelephoneInfoChange = (field: keyof TelephoneInfo, value: string) => {
        const updatedGuest = {
            ...formData,
            telephoneInfo: {
                ...formData.telephoneInfo,
                [field]: value,
            },
        };
        setFormData(updatedGuest);
        updateGuest?.(updatedGuest);
        setIsFormDirty?.(true);
    };

    const getValue = () => formData.identificationInfo?.identification?.idNumber || "";

    const handleTravelDocValueChange = (value: string) => {
        handleIdentificationChange("idNumber", value);
    };

    const handleGenderChange = (selectedGender: "male" | "female") => {
        const updatedGuest = { ...formData, gender: selectedGender };
        setFormData(updatedGuest);
        updateGuest?.(updatedGuest);
        setIsFormDirty?.(true);
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isFormValid = () => {
        if (
            !formData.firstName ||
            !formData.lastName ||
            !formData.placeOfBirth ||
            !formData.nationality
        ) {
            return false;
        }

        if (!formData.dateOfBirth?.day || !formData.dateOfBirth?.month || !formData.dateOfBirth?.year) {
            return false;
        }

        if (!formData.addressInfo?.cityName || !formData.addressInfo?.country) {
            return false;
        }

        if (requireContact && !formData.emailInfo?.email) {
            return false;
        }

        if (!formData.identificationInfo?.identification?.idNumber) {
            return false;
        }

        if (!dobSlotAgeIssue.ok) {
            return false;
        }

        return true;
    };

    useEffect(() => {
        if (!requireContact && isBlankEmail(formData.emailInfo?.email)) {
            setEmailError(false);
            setEmailHelperText("");
        }
    }, [requireContact, formData.emailInfo?.email]);

    useEffect(() => {
        setIsFormValid?.(isFormValid());
        // isFormValid is recreated each render; the listed deps are the inputs it reads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, dobSlotAgeIssue, requireContact]);

    useEffect(() => {
        if (setIsFormValid) {
            const valid = isFormValid();
            setIsFormValid(valid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, dobSlotAgeIssue, requireContact]);

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: "1200px",
                //backgroundColor: "#FAFAFA",
                borderRadius: 0,
                //boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                height: "auto",
                overflow: "hidden",
                mt: 3,
                position: "relative",
            }}
        >
            {/* Title, Name, and Gender Row */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    mb: 4,
                    mt: 2,
                    flexWrap: 'wrap',
                }}
            >
                {/* Group Title + GenderToggle into a row on mobile, but break them up on desktop */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flex: 1,
                        flexDirection: { xs: 'row', md: 'row' }, // both in a row
                        order: { xs: 0, md: 0 },
                        width: { xs: '100%', md: 'auto' },
                    }}
                >
                    {/* Title */}
                    <TextField
                        select
                        disabled={isFormDisabled}
                        label={tGuestDetails("guestTitleInputLabel")}
                        value={titleCodeForSelect}
                        onChange={(e) => handleChange("title", e.target.value)}
                        variant="outlined"
                        SelectProps={{ MenuProps: selectMenuProps }}
                        sx={{
                            flex: { xs: 1, md: 0.5 },
                            minWidth: { md: 165 },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: "var(--oc-paper-bg)",
                                "& .MuiOutlinedInput-input": { color: "var(--oc-text)" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BrandColors.primary },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: BrandColors.primary,
                                    borderWidth: "2px",
                                },
                            },
                            "& .MuiInputLabel-root": {
                                fontSize: "0.8rem",
                                color: "var(--oc-text-muted)",
                                "&.Mui-focused": { color: BrandColors.primary },
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>{tGuestDetails("guestTitleNoneOption")}</em>
                        </MenuItem>
                        {Object.entries(state.titles).map(([code, label]) => (
                            <MenuItem key={code} value={code}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {/* Gender Toggle */}
                    <Box sx={{ flexShrink: 0, display: { xs: 'block', md: 'none' } }}>
                        <GenderToggle
                            disabled={isFormDisabled}
                            gender={genderForToggle}
                            onChange={handleGenderChange}
                        />

                    </Box>
                </Box>

                {/* First Name */}
                <TextField
                    required
                    fullWidth
                    disabled={isFormDisabled}
                    label={tGuestDetails("guestFirstNameInputLabel")}
                    value={formData.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    variant="outlined"
                    sx={{
                        flex: { xs: 1, md: 1.5 },
                        order: { xs: 1, md: 1 },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "var(--oc-paper-bg)",
                            "& .MuiOutlinedInput-input": { color: "var(--oc-text)" },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: BrandColors.primary,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: BrandColors.primary,
                                borderWidth: "2px",
                            },
                        },
                        "& .MuiInputLabel-root": {
                            fontSize: "0.8rem",
                            color: "var(--oc-text-muted)",
                            "&.Mui-focused": {
                                color: BrandColors.primary,
                            },
                        },
                    }}
                />

                {/* Middle Name */}
                <TextField

                    fullWidth
                    label={tGuestDetails("guestMiddleNameInputLabel")}
                    value={formData.middleName}
                    onChange={(e) => handleChange("middleName", e.target.value)}
                    disabled={isFormDisabled}
                    variant="outlined"
                    sx={{
                        flex: { xs: 1, md: 1.5 },
                        order: { xs: 2, md: 2 },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "var(--oc-paper-bg)",
                            "& .MuiOutlinedInput-input": { color: "var(--oc-text)" },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: BrandColors.primary,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: BrandColors.primary,
                                borderWidth: "2px",
                            },
                        },
                        "& .MuiInputLabel-root": {
                            fontSize: "0.9rem",
                            color: "var(--oc-text-muted)",
                            "&.Mui-focused": {
                                color: BrandColors.primary,
                            },
                        },
                    }}
                />

                {/* Last Name */}
                <TextField
                    required
                    fullWidth
                    label={tGuestDetails("guestLastNameInputLabel")}
                    value={formData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    disabled={isFormDisabled}
                    variant="outlined"
                    sx={{
                        flex: { xs: 1, md: 1.5 },
                        order: { xs: 3, md: 3 },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "var(--oc-paper-bg)",
                            "& .MuiOutlinedInput-input": { color: "var(--oc-text)" },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: BrandColors.primary,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: BrandColors.primary,
                                borderWidth: "2px",
                            },
                        },
                        "& .MuiInputLabel-root": {
                            fontSize: "0.9rem",
                            color: "var(--oc-text-muted)",
                            "&.Mui-focused": {
                                color: BrandColors.primary,
                            },
                        },
                    }}
                />

                {/* Gender Toggle for desktop ONLY (after all name fields) */}
                <Box
                    sx={{
                        flexShrink: 0,
                        display: { xs: 'none', md: 'block' },
                        order: { xs: 'unset', md: 4 },
                    }}
                >
                    <GenderToggle
                        disabled={isFormDisabled}
                        gender={genderForToggle}
                        onChange={handleGenderChange}
                    />
                </Box>
            </Box>


            {/* Main Form Layout - 3 Columns */}
            <Grid container spacing={3}>
                {/* Travel Document Section - Left Column */}
                <Grid item xs={12} md={4}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            color: BrandColors.primary,
                            mb: 1,
                            textTransform: "uppercase",
                        }}
                    >
                        {tGuestDetails("travelDocument")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "var(--oc-text-muted)" }}>
                        {tGuestDetails("travelDocumentSubheader")}
                    </Typography>

                    <Grid container spacing={1}>
                        {/* Select Field */}
                        <Grid item xs={6}>
                            <FormControl
                                required
                                variant="standard"
                                fullWidth
                                sx={{
                                    mt: 4.5,
                                    fontSize: "12px",
                                    "& .MuiInputLabel-root": {
                                        paddingLeft: "12px",
                                        color: "var(--oc-text-muted)",
                                        fontSize: "1rem",
                                        "&.Mui-focused": {
                                            color: BrandColors.primary,
                                        },
                                    },
                                    "& .MuiSelect-select": {
                                        paddingLeft: "12px !important",
                                        color: "var(--oc-text)",
                                    },
                                    "& .MuiSelect-icon": {
                                        color: "var(--oc-text-muted)",
                                    },
                                    "& .MuiInput-underline:before": {
                                        borderBottomColor: "var(--oc-text-muted)",
                                    },
                                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                        borderBottomColor: BrandColors.primary,
                                    },
                                    "& .MuiInput-underline:after": {
                                        borderBottomColor: BrandColors.primary,
                                    },
                                }}
                            >
                                <InputLabel shrink={!!formData.identificationInfo?.identification?.idType}
                                    sx={{ color: "var(--oc-text-muted)" }} >
                                    {tGuestDetails("travelDocumentTypeDropdownLabel")}
                                </InputLabel>
                                <Select
                                    value={formData.identificationInfo?.identification?.idType || ""}
                                    onChange={(e) => handleIdentificationChange("idType", e.target.value)}
                                    disabled={isFormDisabled}
                                    MenuProps={selectMenuProps}
                                >
                                    {Object.entries(IDENTIFICATION_TYPES_MAP).map(([code, fallbackLabel]) => (
                                        <MenuItem key={code} value={code}>
                                            {tGuestDetails(`idTypes.${code}`, { defaultValue: fallbackLabel })}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Dynamic TextField */}
                        <Grid item xs={6}>
                            <TextField
                                required
                                fullWidth
                                variant="standard"
                                label={tGuestDetails("documentNumber")}
                                value={getValue()}
                                onChange={(e) => handleTravelDocValueChange(e.target.value)}
                                disabled={!formData.identificationInfo?.identification?.idType || isFormDisabled}
                                InputLabelProps={{
                                    shrink: !!getValue(),
                                }}
                                sx={{
                                    mt: 4.5,
                                    fontSize: "17px",
                                    "& .MuiInput-root": {
                                        paddingLeft: "12px",
                                    },
                                    ...standardInputValueSx,
                                    "& .MuiInput-underline:before": {
                                        borderBottomColor: "var(--oc-text-muted)",
                                        borderBottomStyle: "solid",
                                    },
                                    "& .MuiInput-underline.Mui-disabled:before": {
                                        borderBottomColor: "var(--oc-text-muted)",
                                        borderBottomStyle: "solid",
                                        borderBottomWidth: "1px",
                                    },
                                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                        borderBottomColor: BrandColors.primary,
                                    },
                                    "& .MuiInput-underline:after": {
                                        borderBottomColor: BrandColors.primary,
                                    },
                                    "& .MuiInputLabel-root": {
                                        paddingLeft: "12px",
                                        color: "var(--oc-text-muted)",
                                        fontSize: "1rem",
                                        "&.Mui-focused": {
                                            color: BrandColors.primary,
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    {/*TODO - add COPY button when !primary guest*/}

                    <Button
                        variant="contained"
                        fullWidth
                        sx={{
                            borderRadius: 0,
                            mt: 3,
                            backgroundColor: BrandColors.accent,
                            color: "#FFFFFF",
                            py: 1.5,
                            "&:hover": { backgroundColor: BrandColors.accent },
                        }}
                        onClick={() => {
                            //console.log("scan for ", guestId);
                            navigate(getLocalizedPath(`scan-doc/${guestId}`), {
                                // 🔑 carry the intent explicitly to the scan page
                                state: {
                                    originGuestId: guestId,
                                    scanSession: Date.now(), // unique per click
                                },
                            });
                        }}
                    >
                        {tGuestDetails("scanDocumentButton", { defaultValue: "SCAN DOCUMENT" })}
                    </Button>
                </Grid>
                {/* Middle Column - Personal Details */}
                <Grid item xs={12} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label={tGuestDetails("dateOfBirth")}
                            format="DD/MM/YYYY"
                            value={dateOfBirthValue()}
                            onChange={(v) => {
                                if (!v || !v.isValid()) {
                                    handleChange("dateOfBirth", {
                                        day: null,
                                        month: null,
                                        year: null,
                                    });
                                    return;
                                }
                                handleChange("dateOfBirth", {
                                    day: v.date(),
                                    month: v.month() + 1,
                                    year: v.year(),
                                });
                            }}
                            maxDate={dobPickerBoundsMemo.maxDate}
                            minDate={dobPickerBoundsMemo.minDate}
                            disabled={isFormDisabled}
                            slotProps={{
                                textField: {
                                    variant: "standard",
                                    required: true,
                                    fullWidth: true,
                                    error: !dobSlotAgeIssue.ok,
                                    helperText:
                                        dobSlotAgeIssue.ok === false
                                            ? dobSlotAgeIssue.kind === "child_over_max_age"
                                                ? tGuestDetails("dobChildMaxAgeError", {
                                                      arrivalDate: state?.arrivalDate ?? "",
                                                      childMaxAge: childMaxAgeYears ?? 17,
                                                  })
                                                : tGuestDetails("dobAdultMinAgeError", {
                                                      arrivalDate: state?.arrivalDate ?? "",
                                                      adultMinAge: adultMinAgeYears(childMaxAgeYears ?? 17),
                                                  })
                                            : undefined,
                                    sx: {
                                        mt: 0.9,
                                        "& .MuiInput-root": { paddingLeft: "12px" },
                                        ...standardInputValueSx,
                                        "& .MuiInput-underline:before": { borderBottomColor: "var(--oc-text-muted)" },
                                        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                            borderBottomColor: BrandColors.primary,
                                        },
                                        "& .MuiInput-underline:after": { borderBottomColor: BrandColors.primary },
                                        "& .MuiInputLabel-root": {
                                            paddingLeft: "12px",
                                            color: "var(--oc-text-muted)",
                                            fontSize: "1rem",
                                            "&.Mui-focused": { color: BrandColors.primary },
                                        },
                                    },
                                },
                                actionBar: { actions: ["clear", "today"] },
                            }}
                        />
                    </LocalizationProvider>

                    <FormControl
                        required
                        variant="standard"
                        fullWidth
                        sx={{
                            mt: 1,
                            "& .MuiInputLabel-root": {
                                paddingLeft: "12px",
                                color: "var(--oc-text-muted)",
                                fontSize: "1rem",
                                "&.Mui-focused": { color: BrandColors.primary },
                            },
                            "& .MuiSelect-select": {
                                paddingLeft: "12px !important",
                                color: "var(--oc-text)",
                            },
                            "& .MuiSelect-icon": { color: "var(--oc-text-muted)" },
                            "& .MuiInput-underline:before": { borderBottomColor: "var(--oc-text-muted)" },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: BrandColors.primary },
                            "& .MuiInput-underline:after": { borderBottomColor: BrandColors.primary },
                        }}
                    >
                        <InputLabel shrink={!!formData.placeOfBirth}>
                            {tGuestDetails("placeOfBirthInputLabel")}
                        </InputLabel>
                        <Select
                            value={formData.placeOfBirth || ""}
                            onChange={(e) => handleChange("placeOfBirth", e.target.value)}
                            disabled={isFormDisabled}
                            MenuProps={selectMenuProps}
                        >
                            {sortedBirthCountryEntries.map(([code, label]) => (
                                <MenuItem key={code} value={code}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>


                    {/* <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: "bold",
                            color: BrandColors.primary,
                            mt: 2.5,
                            paddingLeft: "12px !important",
                            textTransform: "uppercase",
                        }}
                    >
                        {tGuestDetails("nationalityLabel")}
                    </Typography> */}
                    <FormControl
                        required
                        variant="standard"
                        fullWidth
                        sx={{
                            mt: 1,
                            "& .MuiInputLabel-root": {
                                paddingLeft: "12px",
                                color: "var(--oc-text-muted)",
                                fontSize: "1rem",
                                "&.Mui-focused": { color: BrandColors.primary },
                            },
                            "& .MuiSelect-select": {
                                paddingLeft: "12px !important",
                                color: "var(--oc-text)",
                            },
                            "& .MuiSelect-icon": { color: "var(--oc-text-muted)" },
                            "& .MuiInput-underline:before": { borderBottomColor: "var(--oc-text-muted)" },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: BrandColors.primary },
                            "& .MuiInput-underline:after": { borderBottomColor: BrandColors.primary },
                        }}
                    >
                        <InputLabel shrink={!!formData.nationality}>
                            {tGuestDetails("nationalityLabel")}
                        </InputLabel>
                        <Select
                            value={formData.nationality || ""}
                            onChange={(e) => handleChange("nationality", e.target.value)}
                            disabled={isFormDisabled}
                            MenuProps={selectMenuProps}
                        >
                            {sortedNationalityEntries.map(([code, label]) => (
                                <MenuItem key={code} value={code}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>


                    <TextField
                        fullWidth
                        variant="standard"
                        label={tGuestDetails("phoneNumberInputLabel")}
                        value={formData.telephoneInfo.phoneNumber}
                        onChange={(e) => handleTelephoneInfoChange("phoneNumber", e.target.value)}
                        disabled={isFormDisabled}
                        InputLabelProps={{
                            shrink: !!formData.telephoneInfo.phoneNumber,
                        }}
                        sx={{
                            mt: 2.7,
                            "& .MuiInput-root": {
                                paddingLeft: "12px",
                            },
                            ...standardInputValueSx,
                            "& .MuiInput-underline:before": {
                                borderBottomColor: "var(--oc-text-muted)",
                            },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                borderBottomColor: BrandColors.primary,
                            },
                            "& .MuiInput-underline:after": {
                                borderBottomColor: BrandColors.primary,
                            },
                            "& .MuiInputLabel-root": {
                                paddingLeft: "12px",
                                color: "var(--oc-text-muted)",
                                fontSize: "1rem",
                                "&.Mui-focused": {
                                    color: BrandColors.primary,
                                },
                            },
                        }}
                    />
                    <TextField
                        required={requireContact}
                        fullWidth
                        variant="standard"
                        label={tGuestDetails(requireContact ? "emailInputLabel" : "emailInputLabelOptional")}
                        value={formData.emailInfo?.email || ""}
                        onChange={(e) => handleEmailInfoChange("email", e.target.value)}
                        disabled={isFormDisabled}
                        error={emailError}
                        helperText={emailHelperText}
                        InputLabelProps={{
                            shrink: !!formData.emailInfo?.email,
                        }}
                        sx={{
                            mt: 2,
                            "& .MuiInput-root": {
                                paddingLeft: "12px",
                            },
                            ...standardInputValueSx,
                            "& .MuiInput-underline:before": {
                                borderBottomColor: emailError ? "red" : "var(--oc-text-muted)",
                            },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                borderBottomColor: emailError ? "red" : BrandColors.primary,
                            },
                            "& .MuiInput-underline:after": {
                                borderBottomColor: emailError ? "red" : BrandColors.primary,
                            },
                            "& .MuiInputLabel-root": {
                                paddingLeft: "12px",
                                color: emailError ? "red" : "var(--oc-text-muted)",
                                fontSize: "1rem",
                                "&.Mui-focused": {
                                    color: emailError ? "red" : BrandColors.primary,
                                },
                            },
                        }}
                    />
                </Grid>

                {/* Right Column - Address Details */}
                <Grid item xs={12} md={4}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: "bold",
                            color: BrandColors.primary,
                            mb: 0.5,
                            p: 0,
                            paddingLeft: "12px !important",
                        }}
                    >
                    </Typography>
                    <TextField
                        fullWidth
                        variant="standard"
                        label={tGuestDetails("streetInputLabel")}
                        value={formData.addressInfo?.addressLine || ""}
                        onChange={(e) => handleAddressInfoChange("addressLine", e.target.value)}
                        disabled={isFormDisabled}
                        InputLabelProps={{
                            shrink: !!formData.addressInfo.addressLine,
                        }}
                        sx={{
                            mt: 3,
                            "& .MuiInput-root": {
                                paddingLeft: "12px",
                            },
                            ...standardInputValueSx,
                            "& .MuiInput-underline:before": {
                                borderBottomColor: "var(--oc-text-muted)",
                            },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                borderBottomColor: BrandColors.primary,
                            },
                            "& .MuiInput-underline:after": {
                                borderBottomColor: BrandColors.primary,
                            },
                            "& .MuiInputLabel-root": {
                                paddingLeft: "12px",
                                color: "var(--oc-text-muted)",
                                fontSize: "1rem",
                                "&.Mui-focused": {
                                    color: BrandColors.primary,
                                },
                            },
                        }}
                    />
                    <TextField
                        required
                        fullWidth
                        variant="standard"
                        label={tGuestDetails("cityInputLabel")}
                        value={formData.addressInfo?.cityName || ""}
                        onChange={(e) => handleAddressInfoChange("cityName", e.target.value)}
                        disabled={isFormDisabled}
                        InputLabelProps={{
                            shrink: !!formData.addressInfo.cityName,
                        }}
                        sx={{
                            mt: 1,
                            "& .MuiInput-root": {
                                paddingLeft: "12px",
                            },
                            ...standardInputValueSx,
                            "& .MuiInput-underline:before": {
                                borderBottomColor: "var(--oc-text-muted)",
                            },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                borderBottomColor: BrandColors.primary,
                            },
                            "& .MuiInput-underline:after": {
                                borderBottomColor: BrandColors.primary,
                            },
                            "& .MuiInputLabel-root": {
                                paddingLeft: "12px",
                                color: "var(--oc-text-muted)",
                                fontSize: "1rem",
                                "&.Mui-focused": {
                                    color: BrandColors.primary,
                                },
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        variant="standard"
                        label={tGuestDetails("postalCodeInputLabel")}
                        value={formData.addressInfo?.postalCode || ""}
                        onChange={(e) => handleAddressInfoChange("postalCode", e.target.value)}
                        disabled={isFormDisabled}
                        InputLabelProps={{
                            shrink: !!formData.addressInfo.postalCode,
                        }}
                        sx={{
                            mt: 1,
                            "& .MuiInput-root": {
                                paddingLeft: "12px",
                            },
                            ...standardInputValueSx,
                            "& .MuiInput-underline:before": {
                                borderBottomColor: "var(--oc-text-muted)",
                            },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                borderBottomColor: BrandColors.primary,
                            },
                            "& .MuiInput-underline:after": {
                                borderBottomColor: BrandColors.primary,
                            },
                            "& .MuiInputLabel-root": {
                                paddingLeft: "12px",
                                color: "var(--oc-text-muted)",
                                fontSize: "1rem",
                                "&.Mui-focused": {
                                    color: BrandColors.primary,
                                },
                            },
                        }}
                    />

                    {/* <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: "bold",
                            color: BrandColors.primary,
                            mt: 2,
                            paddingLeft: "12px !important",
                            textTransform: "uppercase",
                        }}
                    >
                        {tGuestDetails("countryLabel")}
                    </Typography> */}
                    <FormControl
                        required
                        variant="standard"
                        fullWidth
                        sx={{
                            mt: 1,
                            "& .MuiInputLabel-root": {
                                paddingLeft: "12px",
                                color: "var(--oc-text-muted)",
                                fontSize: "1rem",
                                "&.Mui-focused": { color: BrandColors.primary },
                            },
                            "& .MuiSelect-select": {
                                paddingLeft: "12px !important",
                                color: "var(--oc-text)",
                            },
                            "& .MuiSelect-icon": { color: "var(--oc-text-muted)" },
                            "& .MuiInput-underline:before": { borderBottomColor: "var(--oc-text-muted)" },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: BrandColors.primary },
                            "& .MuiInput-underline:after": { borderBottomColor: BrandColors.primary },
                        }}
                    >
                        <InputLabel shrink={!!formData.addressInfo?.country}>
                            {tGuestDetails("countryLabel")}
                        </InputLabel>
                        <Select
                            value={formData.addressInfo?.country || ""}
                            onChange={(e) => handleCountryCodeChange(e.target.value)}
                            disabled={isFormDisabled}
                            MenuProps={selectMenuProps}
                        >
                            {sortedAddressCountryEntries.map(([code, label]) => (
                                <MenuItem key={code} value={code}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Newsletter Subscription and Info */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    width: "100%",
                    mt: 5,
                    gap: { xs: 2, md: 0 },
                }}
            >
                {/* Left Box - Checkbox Form */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={isFormDisabled}
                                sx={{
                                    marginRight: 2,
                                    "&.Mui-checked": {
                                        color: BrandColors.accent,
                                    },
                                }}
                            />
                        }
                        label={
                            <Box component="span">
                                <Typography
                                    component="span"
                                    sx={{ fontSize: "0.9rem", color: "var(--oc-text)" }}
                                >
                                    {tGuestDetails("newsletterCheckbox", { brand: BrandText.newsletterCheckboxBrand })}{" "}
                                </Typography>
                                <Link
                                    to={getLocalizedPath("/privacy-policy")}
                                    state={{ fromGuestDetails: true }}
                                    style={{ textDecoration: "underline", color: "var(--oc-text)" }}
                                >
                                    {tGuestDetails("newsletterCheckboxPrivacyPolicyLink")}
                                </Link>
                            </Box>
                        }
                    />
                </Box>

                {/* Right Box - Info Text */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        maxWidth: "500px",
                    }}
                >
                    <InfoOutlinedIcon
                        sx={{ fontSize: "1.7rem", color: BrandColors.accent, marginRight: 2 }}
                    />
                    <Typography variant="body2" sx={{ color: "var(--oc-text)" }}>
                        {tGuestDetails("info")}
                    </Typography>
                </Box>
            </Box>
        </Box >

    );
};

export default GuestDetailsForm;
